import { BehaviorSubject, Subject } from 'rxjs';

import { DEFAULT_TRACKER_OPTIONS } from './constants';
import {
  HdWizardStepMeta,
  HdWizardStepTrackerOptions,
  HdWizardStepTrackerStep,
  HdWizardStepTrackingStrategy,
  HdWizardStepTree,
  HdWizardStepTreeSnapshot,
  HdWizardStorage,
  HdWizardTimeTravelData,
  WizardVersion
} from './interface';


export class HdWizardStepTracker {
  changes = new Subject<HdWizardStepTrackerStep[]>();
  stepTreeSnapshot$ = new BehaviorSubject(this._getDefaultSnapshot());
  currentStepIndex = -1;
  version = WizardVersion.V2;
  treeStepGroups$ = new BehaviorSubject<HdWizardStepTree>(this._stepTree);

  data: any = {};

  complete = false;

  private _steps: HdWizardStepTrackerStep[] = [];

  constructor(
    private _stepTree: HdWizardStepTree,
    private _storageService: HdWizardStorage,
    private _options: HdWizardStepTrackerOptions
  ) {
    this._options = {
      ...DEFAULT_TRACKER_OPTIONS,
      ..._options
    };

    if (this._options.storageKey) {
      const storedData = this._storageService.getStoredData(this._options.storageKey);

      if (typeof storedData === 'object' && storedData !== null) {
        this._steps = storedData.steps || [];
        this.data = storedData.data || {};
        this.currentStepIndex = this._steps.length ? this._steps.length - 1 : -1;
        this.version = storedData.version;
      }
    }

    this._updateStepTreeSnapshot();
  }

  getStepData(path: string): any {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return {};
    }

    return stepRef.data || {};
  }

  saveStepParams(stepMeta: HdWizardStepMeta) {
    const stepRef = this.getStepRefFromPath(stepMeta.path);

    if (!stepRef) {
      return;
    }

    stepRef.params = { ...stepRef.params, ...stepMeta.params };
    this._storeTrackingInfo();
    this.changes.next(this._steps);
    this._updateStepTreeSnapshot();
  }

  saveStepData(path: string, data: any) {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return;
    }

    stepRef.data = { ...stepRef.data, ...data };
    this._storeTrackingInfo();
    this.changes.next(this._steps);
    this._updateStepTreeSnapshot();
  }

  getStepRefFromPath(path: string): HdWizardStepTrackerStep {
    return this._steps.find((step) => {
      return step.path === path;
    });
  }

  pushStep(step: HdWizardStepTrackerStep) {
    this._steps.push(step);
  }

  popStep() {
    this._steps.pop();
  }

  splice(start: number, count?: number) {
    this._steps.splice(start, count || this._steps.length);
  }

  getLength() {
    return this._steps.length;
  }

  getStepIndexFromPath(path: string): number {
    return this._steps.findIndex(step => step.path === path);
  }

  getCurrentStep() {
    if (this.currentStepIndex >= 0) {
      return this._steps[this.currentStepIndex];
    }
  }

  track(stepMeta: HdWizardStepMeta, skipCurrent = false) {
    /**
     * If current step is skipped, drop it and optionally the steps leading up to the step being tracked
     */
    if (skipCurrent && this.currentStepIndex >= 0) {
      const deleteCount = this.getStepIndexFromPath(stepMeta.path) - this.currentStepIndex;

      this._steps.splice(this.currentStepIndex, Math.max(1, deleteCount));
    }

    const path = stepMeta.path;
    const params = stepMeta.params;
    const index = this.getStepIndexFromPath(path);
    const trackingStrategy = this._options.trackingStrategy;

    if (index >= 0) {
      if (trackingStrategy === HdWizardStepTrackingStrategy.DISCARD_ALWAYS) {
        this.splice(index + 1);
      }
      this.currentStepIndex = index;
    } else {
      let stepPosition = this._steps.findIndex((step) => step.next === path);

      stepPosition = stepPosition >= 0 ? stepPosition + 1 : this._steps.length;

      this._steps.splice(stepPosition, 0, {
        path,
        params
      });

      this.currentStepIndex = stepPosition;
    }

    this.changes.next(this._steps);
    this._steps[this.currentStepIndex].complete = false;
    if (stepMeta.isFinal) {
      this.markAsComplete();
    }
    this._storeTrackingInfo();
    this._updateStepTreeSnapshot();
  }

  markCurrentStepDirty(stepPath: string) {
    const trackingStrategy = this._options.trackingStrategy;
    const stepIndex = this._steps.findIndex((step) => step.path === stepPath);

    if (stepIndex < 0) {
      return;
    }

    if (trackingStrategy === HdWizardStepTrackingStrategy.DISCARD_ON_DIRTY) {
      this.splice(stepIndex + 1);
    }
    this._steps[stepIndex].dirty = true;
  }

  updateStepTitle(path: string, title: string) {
    const stepRef = this.getStepRefFromPath(path);

    if (!stepRef) {
      return;
    }

    stepRef.title = title;
    this.changes.next(this._steps);
    this._storeTrackingInfo();
    this._updateStepTreeSnapshot();
  }

  getNextStep(path: string) {
    const stepIndex: number = this.getStepIndexFromPath(path);

    if (stepIndex >= 0 && stepIndex < this._steps.length - 1) {
      return this._steps[stepIndex + 1];
    }
  }

  getPrevStep(path: string) {
    const stepIndex: number = this.getStepIndexFromPath(path);

    if (stepIndex > 0) {
      return this._steps[stepIndex - 1];
    }
  }

  getSteps() {
    return this._steps;
  }

  markAsComplete() {
    this.complete = true;
    this._updateStepTreeSnapshot();
  }

  markStepAsComplete(path: string) {
    const stepRef = this.getStepRefFromPath(path);
    stepRef.complete = true;
    this._updateStepTreeSnapshot();
  }

  private _updateStepTreeSnapshot() {
    const snapshot: HdWizardStepTreeSnapshot = this._getDefaultSnapshot();
    snapshot.complete = this.complete;

    this._steps.forEach((step, stepIndex) => {
      const pathSegments = step.path.split('/');
      let currentNode = snapshot;

      const currentPathSegments = this.currentStepIndex >= 0
        ? this._steps[this.currentStepIndex].path.split('/')
        : [];
      pathSegments.forEach((segment, segmentIndex) => {
        let nodeRef = currentNode.children.find((child) => {
          return child.segment === segment;
        });

        if (!nodeRef) {
          nodeRef = {
            segment,
            children: []
          };
          currentNode.children.push(nodeRef);
        }
        nodeRef.complete = stepIndex < this.currentStepIndex ? true : step.complete;
        nodeRef.active = segment === currentPathSegments[segmentIndex];
        currentNode = nodeRef;
      });
    });
    this.stepTreeSnapshot$.next(snapshot);
  }

  reset() {
    this._steps = [];
    this.currentStepIndex = -1;
    this.complete = false;
    this.data = {};
    /**
     * Only update version when the user lands to initial step.
     */
    this.version = WizardVersion.V2;

    this._updateStepTreeSnapshot();
    this._storeTrackingInfo();
  }

  saveData(key: string | number, value: any) {
    this.data = { ...this.data, [key]: value };
    this._storeTrackingInfo();
  }

  private _storeTrackingInfo() {
    const storageKey = this._options.storageKey;
    if (storageKey) {
      this._storageService.store(storageKey, {
        steps: this._steps,
        data: this.data,
        version: this.version
      });
    }
  }

  private _getDefaultSnapshot() {
    if (!this._stepTree) {
      return {
        segment: '',
        children: [],
        complete: false,
        active: true
      };
    }

    const tree = this.createSnapshotFromGroupsInfo(this._stepTree);
    tree.active = true;
    return tree;
  }

  createSnapshotFromGroupsInfo(stepTree: HdWizardStepTree) {
    const tree: HdWizardStepTreeSnapshot = {
      segment: stepTree.for || '',
      children: [],
      complete: false,
      active: false
    };

    (stepTree.children || []).forEach((child) => {
      tree.children.push(this.createSnapshotFromGroupsInfo(child));
    });

    return tree;
  }

  fastTravel(data: HdWizardTimeTravelData) {
    this._steps = data.steps || [];
    this.currentStepIndex = this._steps.length ? this._steps.length - 1 : -1;
    this.data = data.data || {};
    this._storeTrackingInfo();
    this._updateStepTreeSnapshot();
  }

  get currentStep() {
    if (this.currentStepIndex >= 0) {
      return this._steps[this.currentStepIndex];
    }

    return null;
  }

  updateWizardStepTree(stepGroups: HdWizardStepTree) {
    this._stepTree = stepGroups;
    this.treeStepGroups$.next(stepGroups);
    this._updateStepTreeSnapshot();
  }
}
